import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { getTimeSegments, nameValidator, NotificationService } from '@thehubz/client/core';
import { User, Vacation } from '@thehubz/client/data-access-api';
import { areIntervalsOverlapping } from 'date-fns';

@Component({
  selector: 'thehubz-user-vacation-dialog',
  templateUrl: './user-vacation-dialog.component.html',
  styleUrls: ['./user-vacation-dialog.component.scss'],
})
export class UserVacationDialogComponent implements OnInit {
  /**
   * Form with the data of the new task
   */
  vacationForm!: FormGroup;
  isNew: boolean | undefined;
  minStartDate = new Date(new Date().toDateString());
  minEndDate = new Date(new Date().toDateString());

  timeSegments = getTimeSegments(9, 18, 2);

  constructor(
    private _formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public dialogData: { vacation: Vacation; user: User; vacations: Vacation[]; index?: number },
    public matDialogRef: MatDialogRef<UserVacationDialogComponent>,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    const { vacation, user } = this.dialogData;
    const vacationMessage = this.getVacationMessage(vacation, user);
    this.isNew = !vacation?.title;
    this.vacationForm = this._formBuilder.group({
      title: [vacation?.title || $localize`Out of office`, [Validators.required, Validators.minLength(2), nameValidator]],
      from: [vacation?.from ? new Date(vacation.from) : null, [Validators.required]],
      to: [vacation?.to ? new Date(vacation.to) : null, [Validators.required]],
      startTime: [vacation?.startTime ? vacation?.startTime : null],
      endTime: [vacation?.endTime ? vacation?.endTime : null],
      message: [vacation?.message ? vacation?.message : vacationMessage],
      // recurrence: [vacation?.recurrence ? vacation?.recurrence : ''],
    });
  }

  startDateChange(evt: any) {
    const date = new Date(evt.value);
    if (evt.value) {
      this.minEndDate = new Date(date);
    }
    let toReplace = '';
    if (this.vacationForm.value.message.includes(fromDateToken)) {
      toReplace = fromDateToken;
    } else if (dateRegex.test(this.vacationForm.value.message)) {
      const matches = this.vacationForm.value.message.match(dateRegex) || [];
      toReplace = matches[0];
    }
    if (toReplace.length > 0) {
      this.vacationForm.patchValue({
        message: this.vacationForm.value.message.replace(toReplace, date.toLocaleDateString()),
      });
    }
  }

  endDateChange(evt: any) {
    const date = new Date(evt.value);
    let toReplace = '';
    if (this.vacationForm.value.message.includes(toDateToken)) {
      toReplace = toDateToken;
    } else if (dateRegex.test(this.vacationForm.value.message)) {
      const matches = this.vacationForm.value.message.match(dateRegex) || [];
      toReplace = matches[matches.length - 1];
    }
    if (toReplace.length > 0) {
      this.vacationForm.patchValue({
        message: this.vacationForm.value.message.replace(toReplace, date.toLocaleDateString()),
      });
    }
  }

  onCancelClick() {
    this.matDialogRef.close();
  }

  saveData(): void {
    if (this.checkOverlap(this.vacationForm.value, this.dialogData.vacations, this.dialogData.index)) {
      this.notificationService.addNotification({
        message: $localize`Dates overlap with existing vacation`,
        type: 'failure',
      });
    } else {
      this.matDialogRef.close({ newData: this.isNew, data: this.vacationForm.value });
    }
  }

  /**
   * Get vacation message (for wysiwyg editor)
   * @param vacation
   */
  private getVacationMessage(vacation: Vacation | undefined, user: User): string {
    let message = `${user.firstName} ${user.lastName} is absent from ${fromDateToken} to ${toDateToken}.`;
    if (vacation) {
      message = vacation.message ? vacation.message : message;

      // replace the tokens with the actual dates
      if (vacation.from) {
        const date = new Date(vacation.from);
        message = message.replace(fromDateToken, date.toLocaleDateString());
      }
      if (vacation.to) {
        const date = new Date(vacation.to);
        message = message.replace(toDateToken, date.toLocaleDateString());
      }
    }
    return message;
  }

  /**
   * Check if an overlap exists between the current vacation and the existing vacations
   *
   * @param vacation
   * @param vacations
   */
  public checkOverlap(vacation: Vacation, vacations: Vacation[], index = -1): boolean {
    let overlap = false;
    if (vacation.from && vacation.to) {
      const newVacationFrom = new Date(vacation.from);
      const newVacationTo = new Date(vacation.to);
      const vacationsClone = [...vacations];
      if (index > -1) {
        vacationsClone.splice(index, 1);
      }
      vacationsClone.some((existingVacation) => {
        if (existingVacation.from && existingVacation.to) {
          const existingVacationFrom = new Date(existingVacation.from);
          const existingVacationTo = new Date(existingVacation.to);
          if (
            areIntervalsOverlapping(
              { start: newVacationFrom, end: newVacationTo },
              { start: existingVacationFrom, end: existingVacationTo },
              { inclusive: true },
            )
          ) {
            overlap = true;
          }
        }
      });
    }
    return overlap;
  }
}

/**
 * Tokens for from date
 */
export const fromDateToken = '[fromDate]';

/**
 * Tokens for to date
 */
export const toDateToken = '[toDate]';

/**
 * Date regex for DD/MM/YYYY
 */
export const dateRegex = new RegExp(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g);
